import React, { useMemo, useState } from "react";
import "./ProcessingDashboard.css";
import "../../dashboard/components/dashboard-sections.css";

import { AddProcessingEmployeeData } from "./AddProcessingEmployeeData";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import DashboardSection from "../../dashboard/components/DashboardSection";
import DashboardCard from "../../dashboard/components/DashboardCard";
import { ProcessingIconSvg } from "../../../svgFiles/ProcessingIconSvg";
import { OutputIconSvg } from "../../../svgFiles/OutputIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import { UtilityIconSvg } from "../../../svgFiles/UtilityIconSvg";
import { FlameIconSvg } from "../../../svgFiles/FlameIconSvg";
import { CostIconSvg } from "../../../svgFiles/CostIconSvg";
import {
  useGetProcessingDashboardOverviewQuery,
  useGetProcessingEmployeeQuery,
  useGetProcessingSummaryQuery,
} from "./processingApi";
import { useSearchGasConsumptionsQuery } from "../cooking/gasconsumption/gasConsumptionApi";
import { useGetAllWorkingAreasQuery } from "../../../app/globalApi";
import useTranslator from "../../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";

const ProcessingDashboard = () => {
  const [showAddEmployment, setShowAddEmployment] = useState(false);
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [tab, setTab] = useState("dashboard");
  const { _t } = useTranslator();

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };

  const formatValueWithUnit = (label, value) => {
    const displayValue = value ?? 0;
    const strValue = String(displayValue);

    if (/%/.test(label)) {
      return /%/.test(strValue) ? strValue : `${strValue} %`;
    }
    if (/per\s*kg|\/\s*kg/i.test(label)) {
      return /per\s*kg/i.test(strValue) ? strValue : `${strValue} Per KG`;
    }
    if (/\bkg\b|kilo|weight/i.test(label)) {
      return /\bkg\b/i.test(strValue) ? strValue : `${strValue} KG`;
    }
    return strValue;
  };

  //  ########### DASHBOARD OVERVIEW ##############
  
  const {
    data : overviewData ,
    isLoading : overviewLoading ,
    isFetching : overviewFetching ,
    isError : overviewError
  } = useGetProcessingDashboardOverviewQuery({
    startDate,
    endDate,
  });

  const processing_flow = overviewData?.data?.processing_flow ?? [] ;
  const gas_consumption = overviewData?.data?.gas_consumption ?? [] ;
  const output_and_summary = overviewData?.data?.output_and_summary ?? [] ;
  const processing_metrics = overviewData?.data?.performance_metrics?.processing_metrics ?? [] ;
  const peeler_metrics = overviewData?.data?.performance_metrics?.peeler_metrics ?? [] ;

  // ############## END #################

  const { data: summaryData } = useGetProcessingSummaryQuery({ tab, date });
  const { data: gasConsumptionData } = useSearchGasConsumptionsQuery({
    page: 1,
    date,
  });
  const { data: employeeSummaryData } = useGetProcessingEmployeeQuery({
    date,
  });
  const { data: workingAreasData, isLoading: isWorkingAreasLoading } =
    useGetAllWorkingAreasQuery();

  const receivingAreaId = useMemo(() => {
    if (!workingAreasData?.data) return null;
    const receivingArea = workingAreasData.data.find(
      (area) => area.name === "Processing & Cooking Area",
    );
    return receivingArea ? receivingArea.id : null;
  }, [workingAreasData]);

  const displayDay =
    currentDate === date
      ? _t("today", "Today")
      : formatDateAndTime(date, PROJECT_DATE_FORMAT);

  const processingItems = {
    cases: [
      {
        label: "Cases Processed",
        value: processing_flow?.total_cases_processed || 0,
      },
      { label: "Cases Combined", value: processing_flow?.total_cases_combined ?? 0 },
      // { label: "Total Cases Used", value: "0-re" },
      { label: "Cases Used With Trash", value: processing_flow?.total_cases_in_trash ?? 0 },
    ],
    treys: [
      { label: "Treys Strips", value: processing_flow?.total_strip_treys || 0 },
      { label: "Treys Cheeks", value: processing_flow?.total_cheeks_treys || 0  },
      { label: "Treys Tapita", value: processing_flow?.total_tapita_treys || 0  },
      { label: "Total Treys", value: processing_flow?.total_treys || 0 },
    ],
    carts: [{ label: "Carts", value: processing_flow?.total_carts || 0 }],
  };

  //  ############# OUTPUT AND SUMMARY ################

  const stripsItems = [
    { label: "KG Strip Total - (Night Plus Day Added if Needed)", value: output_and_summary?.strips?.total_strips_kg_taken_off ?? 0 },
    { label: "KG Prime Strip First Sort", value: output_and_summary?.strips?.kg_prime_strips_first_sorting ?? 0 },
    { label: "Reprocessing Made Prime Strip", value: output_and_summary?.strips?.total_prime_strip_from_rework ?? 0 },
    { label: "Humid KG Strip", value: output_and_summary?.strips?.total_strips_from_humid ?? 0 },
    { label: "Humid %", value: output_and_summary?.strips?.total_strip_prcnt_from_humid_rework ?? 0 },
  ];

  const cheeksItems = [
    { label: "KG Cheek Total", value: output_and_summary?.cheeks?.total_cheeks_kg_taken_off ?? 0 },
    { label: "KG Prime Cheek First Sort", value: output_and_summary?.cheeks?.kg_prime_cheeks_first_sorting ?? 0  },
    { label: "Reprocessing Made Prime Cheek", value: output_and_summary?.cheeks?.total_prime_cheeks_from_rework ?? 0  },
    { label: "Humid KG Cheek", value: output_and_summary?.cheeks?.total_cheeks_from_humid ?? 0 },
    { label: "Humid %", value:  output_and_summary?.cheeks?.total_cheeks_prcnt_from_humid_rework ?? 0 },
  ];

  const trashItems = [
    { label: "Trash Sorting", value: output_and_summary?.trash?.trash_sorting ?? 0 },
    { label: "Trash Processing", value: output_and_summary?.trash?.trash_processing ?? 0 },
    { label: "Total", value: output_and_summary?.trash?.total_trash },
  ];

  const totalItems = [
    { label: "Total KG Taken Off", value: output_and_summary?.totals?.total_taken_off ?? 0 },
    { label: "KG Prime Total With Reprocessing", value: output_and_summary?.totals?.prime_total_with_reprocessing_kg ?? 0 },
    { label: "% Prime", value: output_and_summary?.totals?.prime_prcnt ?? 0 },
  ];

  //  ################## END ###############

  //  ################## PERFORMANCE METRICS ##############

  const metricsItems = [
    { label: "KG Per Case Peeled", value: processing_metrics?.kg_per_case_peeled ?? 0 },
    { label: "KG Per Case In Processing", value: processing_metrics?.kg_per_case_in_processing ?? 0 },
    { label: "Cases Per Peeler", value: processing_metrics?.cases_per_peeler ?? 0 },
    { label: "Treys Per Peeler", value: processing_metrics?.treys_per_peeler ?? 0 },
    { label: "Total Cases Per Cart", value: processing_metrics?.total_cases_per_cart ?? 0 },
    {
      label: "Treys Per Case Peeled",
      value : processing_metrics?.treys_per_case_peeled ?? 0
    },
    { label: "Kilos Per Cart", value: processing_metrics?.kilos_per_cart ?? 0 },
    { label: "Kilos Per Trey", value: processing_metrics?.kilos_per_trey ?? 0},
    { label: "Case Per Trey Entered", value: processing_metrics?.case_per_trey_entered ?? 0},
    {
      label: "Case Per Cart",
      value: processing_metrics?.case_per_cart ?? 0,
    },
    { 
      label: "Treys Per Cart",
      value: processing_metrics?.treys_per_cart ?? 0
    },
  ];

  const peelerMetricsItems = [
    { label: "Peelers", value: peeler_metrics?.total_pellers ?? 0 },
    { label: "Cases Processed Per Hour", value: peeler_metrics?.cases_processed_per_hour ?? 0 },
    { label: "Cases Processed Per Person Per Hour", value: peeler_metrics?.cases_processed_per_person_per_hour ?? 0 },
    { label: "Cases Used In Processing Per Hour", value: "0.00" },
  ];

  //  #################### END ####################

  const gasRecord = gasConsumptionData?.data?.[0] || null;

  const parseNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatMetric = (value, fixed = 2) => {
    const parsed = parseNumber(value);
    return parsed.toLocaleString(undefined, {
      minimumFractionDigits: fixed,
      maximumFractionDigits: fixed,
    });
  };

  const ovens = Array.isArray(gasRecord?.ovens)
    ? [...gasRecord.ovens].sort(
        (a, b) => parseNumber(a?.oven_number) - parseNumber(b?.oven_number),
      )
    : [];

  const getOvenLiters = (ovenNo) => {
    const oven = ovens.find(
      (item) => String(item?.oven_number) === String(ovenNo),
    );
    return parseNumber(oven?.liters_consumed);
  };

  const totalLitersUsed =
    parseNumber(gasRecord?.total_liters_consumed) ||
    ovens.reduce((sum, oven) => sum + parseNumber(oven?.liters_consumed), 0);

  const pricePerLiterWithIva = parseNumber(gasRecord?.price_per_l);

  const totalGasPaid =
    parseNumber(gasRecord?.total_group_cost) ||
    ovens.reduce((sum, oven) => sum + parseNumber(oven?.total_cost), 0);

  const ivaGasPaid =
    parseNumber(gasRecord?.iva_gas_paid) || parseNumber(gasRecord?.iva_paid);

  const kgProcessed =
    parseNumber(summaryData?.data?.kg_processed) ||
    parseNumber(summaryData?.data?.total_kg_processed) ||
    parseNumber(summaryData?.data?.total_kg_taken_off);

  const gasCostPerKg =
    kgProcessed > 0
      ? totalGasPaid / kgProcessed
      : parseNumber(gasRecord?.gas_cost_per_kg_processed);

  const usdValue =
    parseNumber(gasRecord?.usd) || parseNumber(gasRecord?.usd_amount);

  const weeklyAverage =
    parseNumber(gasRecord?.weekly_average) ||
    parseNumber(gasRecord?.weekly_avg);

  const gasConsumptionRows = [
    { label: "Oven 1", value: formatMetric(getOvenLiters(1)) },
    { label: "Oven 2", value: formatMetric(getOvenLiters(2)) },
    { label: "Oven 3", value: formatMetric(getOvenLiters(3)) },
    { label: "Oven 4", value: formatMetric(getOvenLiters(4)) },
    { label: "Oven 5", value: formatMetric(getOvenLiters(5)) },
    { label: "Oven 6", value: formatMetric(getOvenLiters(6)) },
    {
      label: "Evaperator",
      value: formatMetric(parseNumber(gasRecord?.evaperator)),
    },
    { label: "Total Liters Used", value: formatMetric(totalLitersUsed) },
    {
      label: "Price (With IVA) / Per Liter",
      value: formatMetric(pricePerLiterWithIva),
    },    
    { label: "Total Gas Paid", value: formatMetric(totalGasPaid) },
    { label: "KG Processerd", value: formatMetric(kgProcessed) },
    {
      label: "Gas Cost Per KG Processed",
      value: formatMetric(gasCostPerKg),
    },    
    { label: "Weekly Average", value: formatMetric(weeklyAverage) },
  ];

  const renderShiftCard = ({
    title,
    icon,
    badge,
    badgeStyle,
    colHeader = "Value",
    rows,
  }) => (
    <div className="pp-shift-card cmn_card">
      <div className="pp-shift-header">
        <div className="pp-shift-title-row">
          {icon && <span className="pp-shift-icon">{icon}</span>}
          <span className="pp-shift-title">{title}</span>
        </div>
        {badge && (
          <span className="pp-shift-badge" style={badgeStyle}>
            {badge}
          </span>
        )}
      </div>
      {rows.map((row, i) => {
        const isTotal = /total/i.test(row.label);
        return (
          <div key={i} className="pp-shift-row">
            <span>{row.label}</span>
            <span style={isTotal ? { color: "#ee7225", fontWeight: 700 } : {}}>
              {formatValueWithUnit(row.label, row.value)}
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {showAddEmployment && (
        <AddProcessingEmployeeData
          show={showAddEmployment}
          setShow={setShowAddEmployment}
          record={employeeSummaryData?.data}
        />
      )}

      <div className="processing_dashboard_toolbar align-items-center mb-4">
        <h5 className="processing_dashboard_toolbar_title m-0">
          {_t("Processing Overview", "Processing Overview")}
        </h5>
        <div className="processing_filter_group">
          <DateRangeFilter
            setDate={setDate}
            onApplyRange={handleApplyRange}
            triggerId="processing-date-range-filter"
          />
        </div>
      </div>

      <div>
        {/* ── Group 1: Processing Flow ─────────────────────────── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <ProcessingIconSvg size={18} color="#60a5fa" />
              {_t("processing_flow", "Processing Flow")}
            </span>
          }
        >
          {/* Cases Group */}
          <div className="db-evap-row mb-3 p-0">
            {processingItems.cases.map((item, i) => (
              <div key={i} className="db-evap-box">
                <div className="db-evap-label">{item.label}</div>
                <div className="db-evap-value">
                  {formatValueWithUnit(item.label, item.value)}
                </div>
              </div>
            ))}
          </div>

          <div className="db-two-col gap-3 ">
            {/* Treys Group */}
            <DashboardCard
              title={_t("treys", "Treys")}
              icon={<ProcessingIconSvg size={16} color="#667085" />}
            >
              <div className="db-evap-row">
                {processingItems.treys.map((item, i) => (
                  <div key={i} className="db-evap-box">
                    <div className="db-evap-label">{item.label}</div>
                    <div className="db-evap-value">
                      {formatValueWithUnit(item.label, item.value)}
                    </div>
                  </div>
                ))}
              </div>
            </DashboardCard>
            {/* Carts Group */}
            <DashboardCard
              title={_t("carts", "Carts")}
              icon={<ProcessingIconSvg size={16} color="#667085" />}
            >
              <div className="db-evap-row">
                {processingItems.carts.map((item, i) => (
                  <div key={i} className="db-evap-box">
                    <div className="db-evap-label">{item.label}</div>
                    <div className="db-evap-value">
                      {formatValueWithUnit(item.label, item.value)}
                    </div>
                  </div>
                ))}
              </div>
            </DashboardCard>
          </div>
        </DashboardSection>

        {/* ── Group 3: Output & Summary ────────────────────────── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <OutputIconSvg size={18} color="#4ade80" />
              {_t("output_and_summary", "Output and Summary")}
            </span>
          }
        >
          <div className="pp-shifts-grid">
            {renderShiftCard({
              title: "Strips",
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <line x1="9" y1="9" x2="9" y2="9.01" />
                  <line x1="15" y1="9" x2="15" y2="9.01" />
                </svg>
              ),
              badge: "Output",
              badgeStyle: { background: "#dcfae6", color: "#067647" },
              rows: stripsItems,
            })}
            {renderShiftCard({
              title: "Cheeks",
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                  <circle cx="15.5" cy="12" r="1.5" />
                  <circle cx="8.5" cy="12" r="1.5" />
                </svg>
              ),
              badge: "Output",
              badgeStyle: { background: "#dcfae6", color: "#067647" },
              rows: cheeksItems,
            })}
            {renderShiftCard({
              title: "Total",
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="9" y1="9" x2="9" y2="15" />
                  <line x1="15" y1="9" x2="15" y2="15" />
                </svg>
              ),
              badge: "Summary",
              badgeStyle: { background: "#fff0ea", color: "#ee7225" },
              rows: totalItems,
            })}
            {renderShiftCard({
              title: "Trash",
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4h6v2" />
                </svg>
              ),
              badge: "Loss",
              badgeStyle: { background: "#fee4e2", color: "#d92d20" },
              rows: trashItems,
            })}
          </div>
        </DashboardSection>
        {/* ── Group 2: Performance Metrics ─────────────────────── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <BarChartIconSvg size={18} color="#60a5fa" />
              {_t("performance_metrics", "Performance Metrics")}
            </span>
          }
        >
          <div className="pp-shifts-grid">
            {renderShiftCard({
              title: "Processing Metrics - Case Based",
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M8 12l3 3 5-5" />
                </svg>
              ),
              badge: "KPI",
              badgeStyle: { background: "#dfeaff", color: "#175cd3" },
              rows: metricsItems,
            })}
            {renderShiftCard({
              title: "Peeler Metrics",
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="7" r="4" />
                  <path d="M3 21v-2a4 4 0 014-4h10a4 4 0 014 4v2" />
                </svg>
              ),
              badge: "Rate",
              badgeStyle: { background: "#dfeaff", color: "#175cd3" },
              rows: peelerMetricsItems,
            })}
          </div>
        </DashboardSection>
        {/* ── Group 4: Gas Consumption ─────────────────────────── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <UtilityIconSvg size={18} color="#fb923c" />
              {_t("gas_consumption", "Gas Consumption")}
            </span>
          }
        >
          <div className="db-avg-card">
            {/* Zone 3: Performance Metrics */}
            <div
              className="db-avg-header"
              style={{ borderTop: "1px solid #f0f1f3" }}
            >
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <BarChartIconSvg size={15} color="#60a5fa" />
                {_t("performance_metrics", "Performance Metrics")}
              </span>
            </div>
            <div className="db-inner-metric-row">
              <div className="db-inner-metric">
                <div className="db-metric-label">Cases Processed</div>
                <div className="db-metric-value">
                  {processing_flow?.total_cases_processed ?? 0}
                </div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">Gas Cost / Case</div>
                <div className="db-metric-value" style={{ color: "#ee7225" }}>
                  {gas_consumption?.gas_cost_per_case ?? 0}$
                </div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">Weekly Average</div>
                <div className="db-metric-value">
                  {gas_consumption?.weekly_gas_consumed_average ?? 0} (L)
                </div>
              </div>
            </div>
            {/* Zone 1: Oven readings grid */}
            <div className="db-avg-header">
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <FlameIconSvg size={15} color="#fb923c" />
                {_t("oven_readings", "Oven Readings")}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: "#667085",
                  fontFamily: "'Public Sans', serif",
                }}
              >
                {_t("liters_consumed", "Liters Consumed")}
              </span>
            </div>

            <div className="db-trash-grid" style={{ padding: "16px 18px" }}>
              {[1, 2, 3, 4, 5, 6 ,7].map((n) => (
                <div key={n} className="db-trash-cell">
                  {n != 7 ? <span>Oven {n}</span> : <span>Evaporator</span> }
                  <span className="db-trash-val">
                    {
                      gas_consumption?.gas_consumption_log?.[`oven_${n}`]?.gas_consumed_liters ?? 0
                    } (L)
                  </span>
                </div>
              ))}
              <div className="db-trash-cell">
                <span style={{ color: "#ee7225", fontWeight: 700 }}>
                  Total Liters
                </span>
                <span
                  className="db-trash-val orange"
                  style={{ color: "#ee7225", fontWeight: 700 }}
                >
                  {gas_consumption?.total_gas_liter_consumed ?? 0} (L)
                </span>
              </div>
            </div>

            {/* Zone 2: Cost breakdown */}
            <div
              className="db-avg-header"
              style={{ borderTop: "1px solid #f0f1f3" }}
            >
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <CostIconSvg size={15} color="#4ade80" />
                {_t("cost_breakdown", "Cost Breakdown")}
              </span>
            </div>
            <div className="db-inner-metric-row">
              <div className="db-inner-metric">
                <div className="db-metric-label">Price / Liter (with IVA)</div>
                <div className="db-metric-value">
                  {gas_consumption?.price_per_liter ?? 0}$
                </div>
              </div>              
              <div className="db-inner-metric">
                <div className="db-metric-label">Total Gas Cost</div>
                <div className="db-metric-value" style={{ color: "#ee7225" }}>
                  {gas_consumption?.total_gas_cost ?? 0}$
                </div>
              </div>              
            </div>
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default ProcessingDashboard;
